import { aimBetween, createSeededRandom, seededShuffle } from './math';
import type {
  Hazard,
  Mirror,
  PatternFamilyId,
  PhysicsWorld,
  Receiver,
  RelayPattern,
  Shard,
  Vec2,
} from './types';

export const ALL_PATTERN_FAMILIES: readonly PatternFamilyId[] = [
  'tutorial-center',
  'tutorial-left',
  'tutorial-right',
  'tutorial-bank',
  'bank-left-wide',
  'bank-right-wide',
  'bank-left-gate',
  'bank-right-gate',
  'double-zig',
  'double-zag',
  'needle-left',
  'needle-right',
];

export const ADVANCED_PATTERN_FAMILIES: readonly PatternFamilyId[] = [
  'bank-left-wide',
  'bank-right-wide',
  'bank-left-gate',
  'bank-right-gate',
  'double-zig',
  'double-zag',
  'needle-left',
  'needle-right',
];

const TUTORIAL_FAMILIES: readonly PatternFamilyId[] = [
  'tutorial-center',
  'tutorial-left',
  'tutorial-right',
  'tutorial-bank',
];

const DEFAULT_MIRROR_THICKNESS = 4;
const DEFAULT_PULSE_RADIUS = 5;

function receiver(id: string, x: number, y: number, radius: number): Receiver {
  return { id, center: { x, y }, radius };
}

function verticalMirror(id: string, x: number, y1: number, y2: number): Mirror {
  return {
    id,
    a: { x, y: y1 },
    b: { x, y: y2 },
    thickness: DEFAULT_MIRROR_THICKNESS,
  };
}

function circleHazard(id: string, x: number, y: number, radius: number): Hazard {
  return { id, kind: 'circle', center: { x, y }, radius };
}

function segmentHazard(
  id: string,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  radius = 5,
): Hazard {
  return { id, kind: 'segment', a: { x: x1, y: y1 }, b: { x: x2, y: y2 }, radius };
}

function shard(id: string, x: number, y: number): Shard {
  return { id, center: { x, y }, radius: 7 };
}

function reflectX(x: number, mirrorSurfaceX: number): number {
  return 2 * mirrorSurfaceX - x;
}

function verticalBankAim(
  source: Vec2,
  target: Vec2,
  mirrorX: number,
  approach: 'from-left' | 'from-right',
): number {
  const clearance = DEFAULT_PULSE_RADIUS + DEFAULT_MIRROR_THICKNESS / 2;
  const surfaceX = approach === 'from-right' ? mirrorX + clearance : mirrorX - clearance;
  return aimBetween(source, { x: reflectX(target.x, surfaceX), y: target.y });
}

function patternShell(
  family: PatternFamilyId,
  index: number,
  source: Receiver,
  target: Receiver,
  mirrors: readonly Mirror[],
  hazards: readonly Hazard[],
  shards: readonly Shard[],
  solutionAim: number,
  expectedBounces: number,
): RelayPattern {
  return {
    id: `${index}-${family}`,
    index,
    family,
    source,
    target,
    mirrors,
    hazards,
    shards,
    requiresBank: expectedBounces > 0,
    expectedBounces,
    solutionAim,
  };
}

export function createPattern(family: PatternFamilyId, index = 0): RelayPattern {
  const prefix = `${index}-${family}`;

  switch (family) {
    case 'tutorial-center': {
      const source = receiver(`${prefix}-source`, 195, 730, 28);
      const target = receiver(`${prefix}-target`, 195, 235, 42);
      return patternShell(
        family,
        index,
        source,
        target,
        [],
        [],
        [shard(`${prefix}-shard-1`, 195, 475)],
        aimBetween(source.center, target.center),
        0,
      );
    }
    case 'tutorial-left': {
      const source = receiver(`${prefix}-source`, 195, 730, 28);
      const target = receiver(`${prefix}-target`, 130, 255, 39);
      return patternShell(
        family,
        index,
        source,
        target,
        [],
        [],
        [shard(`${prefix}-shard-1`, 161, 480)],
        aimBetween(source.center, target.center),
        0,
      );
    }
    case 'tutorial-right': {
      const source = receiver(`${prefix}-source`, 195, 730, 28);
      const target = receiver(`${prefix}-target`, 265, 260, 37);
      return patternShell(
        family,
        index,
        source,
        target,
        [],
        [],
        [shard(`${prefix}-shard-1`, 232, 482)],
        aimBetween(source.center, target.center),
        0,
      );
    }
    case 'tutorial-bank': {
      const source = receiver(`${prefix}-source`, 270, 720, 28);
      const target = receiver(`${prefix}-target`, 210, 250, 35);
      const mirror = verticalMirror(`${prefix}-mirror-1`, 94, 365, 535);
      return patternShell(
        family,
        index,
        source,
        target,
        [mirror],
        [],
        [shard(`${prefix}-shard-1`, 145, 509)],
        verticalBankAim(source.center, target.center, mirror.a.x, 'from-right'),
        1,
      );
    }
    case 'bank-left-wide': {
      const source = receiver(`${prefix}-source`, 278, 735, 27);
      const target = receiver(`${prefix}-target`, 225, 205, 31);
      const mirror = verticalMirror(`${prefix}-mirror-1`, 78, 350, 535);
      return patternShell(
        family,
        index,
        source,
        target,
        [mirror],
        [circleHazard(`${prefix}-hazard-1`, 335, 465, 20)],
        [shard(`${prefix}-shard-1`, 132, 505)],
        verticalBankAim(source.center, target.center, mirror.a.x, 'from-right'),
        1,
      );
    }
    case 'bank-right-wide': {
      const source = receiver(`${prefix}-source`, 112, 735, 27);
      const target = receiver(`${prefix}-target`, 165, 205, 31);
      const mirror = verticalMirror(`${prefix}-mirror-1`, 312, 350, 535);
      return patternShell(
        family,
        index,
        source,
        target,
        [mirror],
        [circleHazard(`${prefix}-hazard-1`, 55, 465, 20)],
        [shard(`${prefix}-shard-1`, 258, 505)],
        verticalBankAim(source.center, target.center, mirror.a.x, 'from-left'),
        1,
      );
    }
    case 'bank-left-gate': {
      const source = receiver(`${prefix}-source`, 278, 740, 27);
      const target = receiver(`${prefix}-target`, 238, 210, 29);
      const mirror = verticalMirror(`${prefix}-mirror-1`, 82, 360, 515);
      return patternShell(
        family,
        index,
        source,
        target,
        [mirror],
        [segmentHazard(`${prefix}-hazard-1`, 185, 548, 340, 548)],
        [shard(`${prefix}-shard-1`, 124, 500), shard(`${prefix}-shard-2`, 160, 332)],
        verticalBankAim(source.center, target.center, mirror.a.x, 'from-right'),
        1,
      );
    }
    case 'bank-right-gate': {
      const source = receiver(`${prefix}-source`, 112, 740, 27);
      const target = receiver(`${prefix}-target`, 152, 210, 29);
      const mirror = verticalMirror(`${prefix}-mirror-1`, 308, 360, 515);
      return patternShell(
        family,
        index,
        source,
        target,
        [mirror],
        [segmentHazard(`${prefix}-hazard-1`, 50, 548, 205, 548)],
        [shard(`${prefix}-shard-1`, 266, 500), shard(`${prefix}-shard-2`, 230, 332)],
        verticalBankAim(source.center, target.center, mirror.a.x, 'from-left'),
        1,
      );
    }
    case 'double-zig': {
      const source = receiver(`${prefix}-source`, 195, 740, 26);
      const target = receiver(`${prefix}-target`, 230, 180, 29);
      const leftMirror = verticalMirror(`${prefix}-mirror-1`, 70, 525, 655);
      const rightMirror = verticalMirror(`${prefix}-mirror-2`, 320, 235, 365);
      const clearance = DEFAULT_PULSE_RADIUS + DEFAULT_MIRROR_THICKNESS / 2;
      const virtualX = reflectX(reflectX(target.center.x, rightMirror.a.x - clearance), leftMirror.a.x + clearance);
      return patternShell(
        family,
        index,
        source,
        target,
        [leftMirror, rightMirror],
        [circleHazard(`${prefix}-hazard-1`, 142, 420, 19)],
        [shard(`${prefix}-shard-1`, 112, 635), shard(`${prefix}-shard-2`, 220, 420)],
        aimBetween(source.center, { x: virtualX, y: target.center.y }),
        2,
      );
    }
    case 'double-zag': {
      const source = receiver(`${prefix}-source`, 195, 740, 26);
      const target = receiver(`${prefix}-target`, 160, 180, 29);
      const rightMirror = verticalMirror(`${prefix}-mirror-1`, 320, 525, 655);
      const leftMirror = verticalMirror(`${prefix}-mirror-2`, 70, 235, 365);
      const clearance = DEFAULT_PULSE_RADIUS + DEFAULT_MIRROR_THICKNESS / 2;
      const virtualX = reflectX(reflectX(target.center.x, leftMirror.a.x + clearance), rightMirror.a.x - clearance);
      return patternShell(
        family,
        index,
        source,
        target,
        [rightMirror, leftMirror],
        [circleHazard(`${prefix}-hazard-1`, 248, 420, 19)],
        [shard(`${prefix}-shard-1`, 278, 635), shard(`${prefix}-shard-2`, 170, 420)],
        aimBetween(source.center, { x: virtualX, y: target.center.y }),
        2,
      );
    }
    case 'needle-left': {
      const source = receiver(`${prefix}-source`, 300, 730, 25);
      const target = receiver(`${prefix}-target`, 160, 210, 27);
      const mirror = verticalMirror(`${prefix}-mirror-1`, 70, 330, 414);
      return patternShell(
        family,
        index,
        source,
        target,
        [mirror],
        [
          segmentHazard(`${prefix}-hazard-1`, 70, 135, 70, 302, 6),
          segmentHazard(`${prefix}-hazard-2`, 70, 442, 70, 650, 6),
        ],
        [shard(`${prefix}-shard-1`, 133, 455)],
        verticalBankAim(source.center, target.center, mirror.a.x, 'from-right'),
        1,
      );
    }
    case 'needle-right': {
      const source = receiver(`${prefix}-source`, 90, 730, 25);
      const target = receiver(`${prefix}-target`, 230, 210, 27);
      const mirror = verticalMirror(`${prefix}-mirror-1`, 320, 330, 414);
      return patternShell(
        family,
        index,
        source,
        target,
        [mirror],
        [
          segmentHazard(`${prefix}-hazard-1`, 320, 135, 320, 302, 6),
          segmentHazard(`${prefix}-hazard-2`, 320, 442, 320, 650, 6),
        ],
        [shard(`${prefix}-shard-1`, 257, 455)],
        verticalBankAim(source.center, target.center, mirror.a.x, 'from-left'),
        1,
      );
    }
  }
}

export function patternToWorld(pattern: RelayPattern): PhysicsWorld {
  return {
    width: 390,
    height: 844,
    receivers: [pattern.source, pattern.target],
    mirrors: pattern.mirrors,
    hazards: pattern.hazards,
    shards: pattern.shards,
  };
}

export function generatePatternSequence(seed: number | string, count: number): RelayPattern[] {
  const requested = Math.max(0, Math.trunc(count));
  const patterns: RelayPattern[] = [];
  const random = createSeededRandom(seed);

  for (const family of TUTORIAL_FAMILIES) {
    if (patterns.length >= requested) return patterns;
    patterns.push(createPattern(family, patterns.length));
  }

  while (patterns.length < requested) {
    const cycle = seededShuffle(ADVANCED_PATTERN_FAMILIES, random);
    for (const family of cycle) {
      if (patterns.length >= requested) break;
      patterns.push(createPattern(family, patterns.length));
    }
  }

  return patterns;
}
